// SHARED FOUNDATION — owned by the page skeleton; frozen for tab agents to
// EDIT (using it everywhere is the point). The one surface primitive for
// customer-page tab content (mock s4 feel):
//   - Desktop (>= sm): a white Card floating on the tab area's grey surface
//     (the surface itself is painted by ContactDetailTabs' panel host).
//   - Mobile (< sm): NO card chrome — a plain padded Box with a bottom
//     divider, so tab content reads as a flat, full-bleed list.
// Keep this tiny and dumb: layout only — no data, no state, no logic.
import { Box, Card } from "@material-ui/core";
import { useContactSurfaceDown } from "./contactSurface";
import type { ReactNode } from "react";
import type { SxProps } from "@material-ui/system";
import type { Theme } from "@material-ui/core/styles";

type TabCardProps = {
  children: ReactNode;
  sx?: SxProps<Theme>;
};

export function TabCard({ children, sx }: TabCardProps): JSX.Element {
  // The SURFACE, not the viewport: this same card renders both on the full contact page
  // and inside the CRM side panel, which is ~420px wide on a 1920 screen. Asking the
  // window there returns the desktop answer and paints a full-page card into a fifth of a
  // page. Falls back to the viewport wherever no surface is provided, so the contact page
  // is unchanged. See `contactSurface.tsx`.
  const isMobile = useContactSurfaceDown("sm");

  if (isMobile) {
    return (
      <Box
        sx={{
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          "&:last-child": { borderBottom: 0 },
          ...(sx as object)
        }}
      >
        {children}
      </Box>
    );
  }

  return <Card sx={{ p: 2.5, ...(sx as object) }}>{children}</Card>;
}
